import { Badge } from "react-bootstrap";
import Alias from "components/controls/alias";
import Utils from "context/utils";
import { sessionStatusClass } from "blob_types/blobhub/compute/workflow/playground/session/sessionStatus";

/**
 * SessionProperties — the shared session properties table rendered by both the Debugger's Session
 * rail (workflow/generic/debugger/session/index.js) and the Playground's Session -> Basics rail
 * (workflow/generic/playground/details/playground.js). Before this extraction the two tables had
 * drifted: the playground had no "X ago" row, and the Created label read "Created" in one and nowhere
 * in the other. This is now the one definition of row order and content for both.
 *
 * Renders just the `<table className="properties">` — not the surrounding `Card.Text`/`SectionLabel`
 * framing, so each caller keeps its own heading and section layout.
 *
 * Row order (fixed, not caller-configurable):
 *   1. Session ID  — the session's UUID, in <code>.
 *   2. Created     — Utils.formatDateTime(session.created_at, timezone), followed by a second,
 *                    unlabelled row carrying Utils.formatTimeAgo(session.created_at) (e.g. "3 hours
 *                    ago"). Always rendered: every session carries created_at from creation onward.
 *   3. Alias       — the shared pill (components/controls/alias); the row is omitted entirely when
 *                    the session has no alias.
 *   4. Description — omitted entirely when the session has no description.
 *   5. Status      — the status badge; sessionStatusClass drives the outlined-pill vs. muted styling.
 *
 * Props:
 *   session        — the session object ({ id, alias?, description?, created_at, status, ... }).
 *                    Required; both callers already gate rendering of this component on a session
 *                    being present.
 *   timezone       — passed straight through to Utils.formatDateTime for the Created row.
 *
 * Pure: no state, no lifecycle, no calls out to shellInterface/Api. A function component rather than
 * a class, matching this directory's other presentational one-offs (ActionCard/ActionCards).
 */
export default function SessionProperties({ session, timezone }) {
  return (
    <table className="properties">
      <tr>
        <td className="prop-name-column">Session ID</td>
        <td className="prop-value-column"><code>{session.id}</code></td>
      </tr>
      <tr>
        <td className="prop-name-column">Created</td>
        <td className="prop-value-column">{Utils.formatDateTime(session.created_at, timezone)}</td>
      </tr>
      <tr>
        <td className="prop-name-column"></td>
        <td className="prop-value-column">{Utils.formatTimeAgo(session.created_at)}</td>
      </tr>
      {session.alias &&
        <tr>
          <td className="prop-name-column">Alias</td>
          <td className="prop-value-column"><Alias value={session.alias} /></td>
        </tr>
      }
      {session.description &&
        <tr>
          <td className="prop-name-column">Description</td>
          <td className="prop-value-column">{session.description}</td>
        </tr>
      }
      <tr>
        <td className="prop-name-column">Status</td>
        <td className="prop-value-column">
          <Badge variant="secondary" className={sessionStatusClass(session.status)}>{session.status}</Badge>
        </td>
      </tr>
    </table>
  );
}
